import React, { useState, useEffect } from "react";
import {
  useAppContext,
  useCartContext,
  useScroll,
  useBreakpoints
} from "~hooks";
import { WidthContainer, ListLinks, SVG, Grid, MobileMenu } from "~components";
import cn from "classnames";
import { Link } from "gatsby";
import { parsePixelsToNumber, scrollToTop } from "~utils/helpers";
import * as styles from "./styles.module.scss";

/** ============================================================================
 * @component
 * Global header component
 */

const Header = ({ menu, bannerText }) => {
  const {
    pathname,
    primaryColor,
    isCartOpen,
    setIsCartOpen,
    isMobileMenuOpen,
    setIsMobileMenuOpen
  } = useAppContext();
  const { hasCartInitialised, numberOfItemsInCart } = useCartContext();
  const { scrollY } = useScroll();
  const breakpoints = useBreakpoints();

  const [isTransparent, setIsTransparent] = useState(false);
  const [menuContentColor, setMenuContentColor] =
    useState(`var(--color-white)`);

  const isHomePage = pathname === `/`;
  const isTransparencyEnabled = isHomePage;
  const isDesktopWidth = breakpoints.largeTablet;

  const bannerHeight = bannerText
    ? parsePixelsToNumber(styles.bannerHeight)
    : 0;
  const headerHeight = parsePixelsToNumber(styles.headerHeight);

  const scrollMargin = bannerText ? bannerHeight : 1;
  const hasPassedScrollMargin = scrollY < scrollMargin;

  const checkIfTransparent = () => {
    if (!isTransparencyEnabled) {
      setIsTransparent(false);
      return;
    }

    setIsTransparent(hasPassedScrollMargin);
  };

  useEffect(() => {
    checkIfTransparent();
  }, [scrollY, pathname]);

  useEffect(() => {
    setMenuContentColor(isTransparent ? primaryColor : `var(--color-white)`);
  }, [isTransparent, primaryColor]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    checkIfTransparent();
    if (isDesktopWidth) {
      setIsMobileMenuOpen(false);
    }
  }, [breakpoints.largeTablet]);

  useEffect(() => {
    if (isCartOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isCartOpen]);

  // Sometimes page loads scrolled just below banner
  useEffect(() => {
    setTimeout(scrollToTop, 200);
  }, []);

  return (
    <div
      className={cn(styles.headerContainer, {
        [styles.removeTopMargin]: isTransparencyEnabled,
        [styles.hasBanner]: bannerText
      })}
    >
      {bannerText && isHomePage && (
        <div
          className={cn(styles.banner, `b2`)}
          style={{
            background: primaryColor
          }}
        >
          {bannerText}
        </div>
      )}
      <div
        className={cn(styles.fixedContainer, {
          [styles.hasBanner]: bannerText,
          [styles.fixed]: !isTransparent
        })}
      >
        <header
          style={{ color: isMobileMenuOpen ? `white` : menuContentColor }}
          className={cn(styles.headerBar, {
            [styles.transparent]: isTransparent && !isMobileMenuOpen
          })}
        >
          <WidthContainer paddingOnly>
            <div className={styles.content}>
              <Grid>
                <div className={styles.desktopLeft}>
                  <Link
                    aria-label="Home"
                    to="/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={styles.homeLink}
                  >
                    <SVG svg="wordmark" className={styles.wordmark} />
                  </Link>
                </div>

                <nav className={styles.desktopNav}>
                  <ul className="button-text">
                    <ListLinks links={menu?.links} />
                  </ul>
                </nav>

                <div className={styles.rightColumn}>
                  <button
                    type="button"
                    onClick={() => setIsCartOpen(true)}
                    aria-label="Open cart"
                    className={cn(styles.cartButton, {
                      [styles.visible]: hasCartInitialised
                    })}
                  >
                    <div className={styles.icon}>
                      <SVG svg="cart" className={styles.cartIcon} />
                    </div>
                    <span className={cn(`button-text`, styles.desktopMenuItem)}>
                      Cart ({numberOfItemsInCart})
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                    className={styles.icon}
                    aria-label="Open menu"
                  >
                    <div className={styles.hamburger}>
                      <div
                        className={cn(styles.line, {
                          [styles.active1]: isMobileMenuOpen
                        })}
                      />
                      <div
                        className={cn(styles.line, {
                          [styles.active2]: isMobileMenuOpen
                        })}
                      />
                      <div
                        className={cn(styles.line, {
                          [styles.active3]: isMobileMenuOpen
                        })}
                      />
                    </div>
                  </button>
                </div>
              </Grid>
            </div>
          </WidthContainer>
        </header>
        <MobileMenu
          primaryColor={primaryColor}
          hasPassedScrollMargin={hasPassedScrollMargin}
          bannerHeight={bannerHeight}
          headerHeight={headerHeight}
          scrollY={scrollY}
          isMobileMenuOpen={isMobileMenuOpen}
          bannerText={bannerText}
          menu={menu}
          isTransparencyEnabled={isTransparencyEnabled}
        />
      </div>
    </div>
  );
};

export default Header;
